// Reusable Color Picker Component

import React, { useState, useRef, useEffect } from 'react';
import { ColorPickerProps } from '../../types/colorTypes';
import { isValidHexColor } from '../../utils/colorUtilities';
import './ColorPicker.css';

export const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  color,
  onChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(color);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Predefined color palette
  const presetColors = [
    '#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0', '#fce4ec',
    '#e0f2f1', '#f1f8e9', '#fff8e1', '#e8eaf6', '#f9fbe7',
    '#1976d2', '#7b1fa2', '#388e3c', '#f57c00', '#c2185b',
    '#00695c', '#689f38', '#ffa000', '#512da8', '#303f9f'
  ];

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update input value when color prop changes
  useEffect(() => {
    setInputValue(color);
  }, [color]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (isValidHexColor(value)) {
      onChange(value);
    }
  };

  const handleInputBlur = () => {
    if (!isValidHexColor(inputValue)) {
      setInputValue(color); // Reset to valid color
    }
  };

  const handlePresetColorClick = (presetColor: string) => {
    setInputValue(presetColor);
    onChange(presetColor);
    setIsOpen(false);
  };

  const isInputValid = isValidHexColor(inputValue);

  return (
    <div className="color-picker" ref={dropdownRef}>
      <label className="color-picker-label">
        {label}
      </label>
      
      <div className="color-picker-input-container">
        <button
          className={`color-preview ${disabled ? 'disabled' : ''}`}
          style={{ backgroundColor: isInputValid ? inputValue : '#ccc' }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-label={`Current color: ${color}`}
        />
        
        <input
          ref={inputRef}
          type="text"
          className={`color-input ${!isInputValid ? 'invalid' : ''}`}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          placeholder="#000000"
          disabled={disabled}
          maxLength={7}
        />
        
        <input
          type="color"
          className="native-color-picker"
          value={isInputValid ? inputValue : color}
          onChange={(e) => handleInputChange(e.target.value)}
          disabled={disabled}
        />
      </div>

      {!isInputValid && (
        <div className="color-picker-error">
          Please enter a valid hex color (e.g., #ff0000)
        </div>
      )}

      {isOpen && !disabled && (
        <div className="color-picker-dropdown">
          <div className="color-picker-header">
            <h6>Preset Colors</h6>
          </div>
          
          <div className="preset-colors-grid">
            {presetColors.map((presetColor) => (
              <button
                key={presetColor}
                className={`preset-color ${color === presetColor ? 'selected' : ''}`}
                style={{ backgroundColor: presetColor }}
                onClick={() => handlePresetColorClick(presetColor)}
                title={presetColor}
                aria-label={`Select color ${presetColor}`}
              />
            ))}
          </div>
          
          <div className="color-picker-footer">
            <button
              className="random-color-btn"
              onClick={() => {
                const randomColor = presetColors[Math.floor(Math.random() * presetColors.length)];
                handlePresetColorClick(randomColor);
              }}
            >
              Random Color
            </button>
          </div>
        </div>
      )}
    </div>
  );
};